/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useMemo } from 'react';
import { CheckboxProps } from '@g17eco/molecules/form';
import { CardGridViewMode } from '@features/survey-scope/CardGrid';
import { UtrvStatus } from '../constants/status';
import { SurveyGroupsData } from '../model/surveyData';
import {
  convertToQuestionList,
  createScopeQuestionGroup,
  createScopeQuestionGroupOptionalValue,
  initializeScopeGroupCustomMetrics,
  initializeScopeGroupFrameworks,
  initializeScopeGroupFrameworkSubGroups,
  initializeScopeGroupSDG,
  initializeScopeGroupStandards,
  scopeGroupProcessor,
  surveyGroupsByScopeGroupsProcessor,
  surveyGroupsByScopeSubGroupsProcessor,
  surveyGroupsBySDGAndScopeProcessor,
} from '../selectors/survey';
import { Materiality, MaterialityMap } from '../types/initiative';
import {
  AllowedAggregatedUtrvStatus,
  BaseQuestionGroup,
  ScopeCategoryGroups,
  ScopeQuestionGroup,
  ScopeQuestionGroupOptionalValue,
  SurveyModelMinData,
  SurveySettings,
  ScopeQuestionOptionalValue,
  ScopeQuestion,
  AsyncProcess,
  AsyncProcessType,
  AsyncProcessStatus,
} from '@g17eco/types/survey';
import { DATE, formatDateUTC, getFullUTCDate, getUTCEndOf, getUtcDateUnit } from './date';
import { DataPeriods, getPeriodName } from './dataPeriods';
import { QUESTION, SURVEY } from '@constants/terminology';
import { UtrvFilter } from '@g17eco/types/insight-custom-dashboard-filter';
import { SurveySummary } from '@g17eco/types/scorecard';
import { UniversalTrackerValuePlain } from '@g17eco/types/universalTrackerValue';
import { Option } from '../molecules/select/types';
import { ConfigurationType } from '../features/question-configuration/types';
import { ScopeGroups, SurveyType } from '../types/surveyCommon';
import { SurveyOverviewMode } from '@g17eco/types/surveyOverview';
import type { ActiveFeatures } from '@g17eco/core';
import { SurveyActionData } from '@g17eco/types/surveyAction';

export function getInitialSurveySettings(): SurveySettings {
  return {
    filterByStatus: [],
    filterByDelegationStatus: [],
    filterByGoal: [],
    filterByRegulatory: [],
    filterByModules: [],
    filterByRole: [],
    filterByUser: [],
    filterByTag: [],
    searchText: '',
    groupBy: ['sdg'],
    viewLayout: CardGridViewMode.gallery,
    filterByBookmarks: false,
  };
}

export const convertSurveyMaterialityToUtrCodeMap = (materialityMap?: MaterialityMap): MaterialityMap | undefined => {
  if (!materialityMap) {
    return;
  }
  return {
    [Materiality.Low]: materialityMap[Materiality.Low].map((c) => `sdg/${c}`),
    [Materiality.Medium]: materialityMap[Materiality.Medium].map((c) => `sdg/${c}`),
    [Materiality.High]: materialityMap[Materiality.High].map((c) => `sdg/${c}`),
    [Materiality.None]: materialityMap[Materiality.None].map((c) => `sdg/${c}`),
  };
};

export interface ProgressStats {
  completed: number;
  count: number;
  verified: number;
  rejected: number;
  updated: number;
  created: number;
}

export function getSurveyQuestionProgress(surveyGroups: ScopeQuestionGroup[]): ProgressStats {
  const progressStats: ProgressStats = { completed: 0, count: 0, verified: 0, rejected: 0, updated: 0, created: 0 };
  if (!surveyGroups) {
    return progressStats;
  }

  const uniqueIds = new Set();

  surveyGroups.forEach((questionGroup) => {
    questionGroup.list.forEach((question) => {
      // group contains duplicates, need to only count unique questions
      if (uniqueIds.has(question.utrv._id)) {
        return;
      }
      progressStats.count += 1;
      progressStats.completed += [UtrvStatus.Verified, UtrvStatus.Updated].includes(question.utrv.status as UtrvStatus)
        ? 1
        : 0;
      progressStats.verified += [UtrvStatus.Verified].includes(question.utrv.status as UtrvStatus) ? 1 : 0;
      progressStats.rejected += question.utrv.status === UtrvStatus.Rejected ? 1 : 0;
      progressStats.updated += question.utrv.status === UtrvStatus.Updated ? 1 : 0;
      progressStats.created += question.utrv.status === UtrvStatus.Created ? 1 : 0;
      uniqueIds.add(question.utrv._id);
    });
  });
  return progressStats;
}

export function getSurveyQuestionProgressFromUtrvs(
  utrvs: Pick<UniversalTrackerValuePlain, 'status' | '_id'>[]
): ProgressStats {
  const progressStats: ProgressStats = { completed: 0, count: 0, verified: 0, rejected: 0, updated: 0, created: 0 };
  if (!utrvs) {
    return progressStats;
  }

  const uniqueIds = new Set();

  utrvs.forEach((utrv) => {
    // group contains duplicates, need to only count unique questions
    if (uniqueIds.has(utrv._id)) {
      return;
    }
    progressStats.count += 1;
    progressStats.completed += [UtrvStatus.Verified, UtrvStatus.Updated].includes(utrv.status as UtrvStatus) ? 1 : 0;
    progressStats.verified += [UtrvStatus.Verified].includes(utrv.status as UtrvStatus) ? 1 : 0;
    progressStats.rejected += utrv.status === UtrvStatus.Rejected ? 1 : 0;
    progressStats.updated += utrv.status === UtrvStatus.Updated ? 1 : 0;
    progressStats.created += utrv.status === UtrvStatus.Created ? 1 : 0;
    uniqueIds.add(utrv._id);
  });
  return progressStats;
}

export function useUtrSurveyGroupsOptionalValue(
  overviewMode: SurveyOverviewMode,
  surveyData: SurveyGroupsData | undefined
): ScopeQuestionGroupOptionalValue[] {
  const { blueprintGroups, questionList } = useMemo(() => {
    if (!surveyData) {
      return { blueprintGroups: [], questionList: [] };
    }

    const scopeGroups = createScopeQuestionGroupOptionalValue(surveyData);
    const questionList = new Map<string, (typeof scopeGroups)[0]['list'][0]>();

    scopeGroups?.forEach((questionGroup) => {
      questionGroup.list.forEach((question) => {
        questionList.set(question.universalTracker.getId(), question);
      });
    });
    return { blueprintGroups: scopeGroups, questionList: Array.from(questionList.values()) };
  }, [surveyData]);

  if (!surveyData) {
    return [];
  }

  return getSurveyGroups(overviewMode, surveyData, blueprintGroups, questionList);
}

export const useSurveyGroups = (
  overviewMode: SurveyOverviewMode,
  surveyData?: SurveyActionData
): ScopeQuestionGroup[] => {
  const [blueprintGroups, questionList] = useMemo(() => {
    if (!surveyData) {
      return [[], []];
    }
    const blueprintGroups = createScopeQuestionGroup(surveyData);
    const questionList = convertToQuestionList(blueprintGroups);
    return [blueprintGroups, questionList];
  }, [surveyData]);

  if (!surveyData) {
    return [];
  }

  return getSurveyGroups(overviewMode, surveyData, blueprintGroups, questionList);
};

export function getSurveyGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  overviewMode: SurveyOverviewMode,
  surveyData: SurveyGroupsData<T>,
  blueprintGroups: BaseQuestionGroup<T>[],
  questionList: T[],
  { isShowingMappedMetrics = false, features }: { isShowingMappedMetrics?: boolean; features?: ActiveFeatures } = {},
) {
  switch (overviewMode) {
    case SurveyOverviewMode.ScopeGroups:
      return getSurveyGroupsByScopeCategories(surveyData, questionList, isShowingMappedMetrics, features);
    case SurveyOverviewMode.Scope:
      return getSurveyGroupsByScopeSubCategories(surveyData, questionList);
    case SurveyOverviewMode.SdgAndScope:
      return getSurveyGroupsBySDGAndScope(surveyData, questionList);
    case SurveyOverviewMode.QuestionList:
      return [{ groupName: '', list: questionList }];
    case SurveyOverviewMode.Universal:
    default:
      return blueprintGroups;
  }
}

function initializeScopeGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData
): ScopeCategoryGroups<T> {
  return {
    [ScopeGroups.Custom]: initializeScopeGroupCustomMetrics(surveyData),
    [ScopeGroups.Sdg]: initializeScopeGroupSDG(),
    // Materiality scope is saved on surveyData as SDGs, so there is confusion when displaying scope groups
    // to figure out if the user originally selected SDGs or Materiality, which causes BOTH to be displayed.
    // As a dirty hack, we just will never show materiality as groups
    [ScopeGroups.Materiality]: {}, //initializeScopeGroupMateriality(),
    [ScopeGroups.Standards]: initializeScopeGroupStandards(),
    [ScopeGroups.Frameworks]: initializeScopeGroupFrameworks(),
  };
}

function initializeScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData
) {
  return {
    [ScopeGroups.Custom]: initializeScopeGroupCustomMetrics<T>(surveyData),
    [ScopeGroups.Sdg]: initializeScopeGroupSDG<T>(),
    // Materiality scope is saved on surveyData as SDGs, so there is confusion when displaying scope groups
    // to figure out if the user originally selected SDGs or Materiality, which causes BOTH to be displayed.
    // As a dirty hack, we just will never show materiality as groups
    [ScopeGroups.Materiality]: {}, //initializeScopeGroupMateriality(),
    [ScopeGroups.Standards]: initializeScopeGroupStandards<T>(),
    [ScopeGroups.Frameworks]: initializeScopeGroupFrameworkSubGroups<T>(),
  };
}

function getScopeCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  questionList: T[]
) {
  const emptyScopeGroups = initializeScopeGroups<T>(surveyData);
  return scopeGroupProcessor(surveyData, questionList, emptyScopeGroups);
}

function getSurveyGroupsByScopeCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  questionList: T[],
  isShowingMappedMetrics: boolean = false,
  features?: ActiveFeatures,
) {
  return surveyGroupsByScopeGroupsProcessor<T>(
    surveyData,
    getScopeCategories(surveyData, questionList),
    isShowingMappedMetrics,
    features,
  );
}

function getScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  questionList: T[]
) {
  const emptyScopeGroups = initializeScopeSubCategories<T>(surveyData);
  return scopeGroupProcessor(surveyData, questionList, emptyScopeGroups);
}

function getSurveyGroupsByScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  questionList: T[]
) {
  return surveyGroupsByScopeSubGroupsProcessor(surveyData, getScopeSubCategories<T>(surveyData, questionList));
}

function getSurveyGroupsBySDGAndScope<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  surveyData: SurveyGroupsData<T>,
  questionList: T[]
) {
  return surveyGroupsBySDGAndScopeProcessor<T>(surveyData, getScopeCategories(surveyData, questionList));
}

export const getCheckOptions: (canAccessVerification: boolean) => Pick<CheckboxProps, 'groupCode' | 'options'> = (
  canAccessVerification
) => ({
  groupCode: 'advancedOptions',
  options: [
    {
      code: 'verificationRequired',
      name: 'Verification required',
      instructions: `All ${QUESTION.PLURAL} must be separately verified by someone in your organisation. This can provide additional credibility and enforces data governance.`,
      disabled: !canAccessVerification,
    },
    {
      code: 'evidenceRequired',
      name: 'Evidence required',
      instructions: `All ${QUESTION.PLURAL} must be submitted with supporting evidence, such as a photo, file, or link. This can provide substantiating proof of the data claim.`,
    },
    {
      code: 'noteRequired',
      name: 'Further explanation required',
      instructions: `All ${QUESTION.PLURAL} must be submitted with text in the Further explanation section. This includes text only ${QUESTION.PLURAL}, which could be unusual for users.`,
    },
    {
      code: 'isPrivate',
      name: 'Mark all data as private',
      instructions: `All submitted data will be hidden from any investors, financial institutions, or regulators that you grant access to. We recommend leaving this off. You can mark individual ${QUESTION.PLURAL} as Private later if you wish to.`,
    },
  ],
});

export const hasSurveyConflict = (
  existingSurveys: Pick<SurveySummary, '_id' | 'initiativeId' | 'type' | 'sourceName' | 'effectiveDate' | 'period'>[],
  props: {
    type: SurveyType;
    initiativeId?: string;
    year?: number;
    month?: number;
    sourceName?: string;
    period?: DataPeriods;
  },
  surveyId: string | undefined
) => {
  return existingSurveys.some((survey) => {
    return (
      surveyId !== survey._id &&
      survey.initiativeId === props.initiativeId &&
      (survey.type ?? SurveyType.Default) === props.type &&
      survey.sourceName === props.sourceName &&
      getUtcDateUnit('month', survey.effectiveDate) === props.month &&
      getUtcDateUnit('year', survey.effectiveDate) === props.year &&
      (survey.period ?? DataPeriods.Yearly) === props.period
    );
  });
};

export function getEffectiveDate(selectedMonth: number, selectedYear: number) {
  let effectiveDate = new Date();
  effectiveDate = getFullUTCDate(selectedYear, selectedMonth - 1, 1, effectiveDate);
  effectiveDate = getUTCEndOf('month', effectiveDate);
  return effectiveDate.toISOString();
}

export const getSurveyTypeLabel = (type: string) => {
  switch (type) {
    case SurveyType.Materiality:
      return 'Materiality assessment';
    case SurveyType.Aggregation:
      return `Combined ${SURVEY.SINGULAR}`;
    case SurveyType.AutoAggregation:
      return `Aggregated ${SURVEY.SINGULAR}`;
    case SurveyType.Default:
    default:
      return `Default ${SURVEY.SINGULAR}`;
  }
};

export const getSurveyTypeOptions = () => [
  {
    label: SURVEY.CAPITALIZED_PLURAL,
    value: SurveyType.Default,
  },
  { label: `Combined ${SURVEY.PLURAL}`, value: SurveyType.Aggregation },
];

export const isAggregatedSurvey = (surveyType: SurveyType | undefined) => {
  if (!surveyType) {
    return false;
  }
  return [SurveyType.Aggregation, SurveyType.AutoAggregation].includes(surveyType);
};

export const isAutoAggregatedSurvey = (surveyType: string | undefined) => {
  if (!surveyType) {
    return false;
  }
  return surveyType === SurveyType.AutoAggregation;
};

export enum SurveySettingOption {
  Configuration = 'configuration',
  Message = 'message',
  Import = 'import',
  ManageAssurance = 'manage-assurance',
}

export enum SurveyFloatingToolbarButton {
  Platform = 'Platform metric overrides',
  NotReporting = 'Not reporting',
}

export const getAdminRouteHiddenOptions = (isRootOrganization: boolean) => {
  return isRootOrganization
    ? [SurveyFloatingToolbarButton.NotReporting]
    : [...Object.values(ConfigurationType), ...Object.values(SurveyFloatingToolbarButton)];
};

export const getFormattedSurveyDate = (item: Pick<SurveyModelMinData, 'effectiveDate' | 'period'>) => {
  return `${formatDateUTC(item.effectiveDate, DATE.MONTH_YEAR)} ${getPeriodName(item.period, false)}`;
};

const metricStatusLabelMap: { [key in AllowedAggregatedUtrvStatus]: string } = {
  [UtrvFilter.AllAnswered]: `All answered ${QUESTION.PLURAL} (answered and verified)`,
  [UtrvFilter.Verified]: `Verified ${QUESTION.PLURAL} only`,
};

export const aggregatedUtrvStatusOptions: Option<AllowedAggregatedUtrvStatus>[] = Object.entries(
  metricStatusLabelMap
).map(([value, label]) => ({
  value: value as AllowedAggregatedUtrvStatus,
  label,
}));

export const hasProcessingProcess = (asyncProcesses: AsyncProcess[] | undefined, processType: AsyncProcessType) => {
  return asyncProcesses?.some(
    (process) => process.type === processType && process.status === AsyncProcessStatus.Processing,
  );
};

export const hasNoProcessingProcess = (asyncProcesses: AsyncProcess[] | undefined) => {
  return asyncProcesses?.every((process) => process.status !== AsyncProcessStatus.Processing);
};
